/**
 * TanStack Query hooks for case-level skip persistence.
 *
 * A reviewer skips a case when the comparison is against an incomplete report
 * (e.g. the report references a separate abdomen/pelvis report we are not
 * comparing against), so its discrepancies are not true misses. Skipped cases
 * fall off the worklist like reviewed ones, revealed by the same toggle.
 *
 * Reads the caller's skips in bulk from `/api/reader/skipped` and writes via
 * `/api/reader/cohort/<cohort>/study/<uid>/skip`. The backend stores idempotent
 * PutItem rows in `a2z-medi-flow`; undo (skipped=false) is a stored tombstone,
 * not an absence of row, but the bulk read excludes tombstones so the frontend
 * treats un-skipped and never-skipped identically.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";

/** Skip-reason vocabulary — the runtime source of truth. `SkipReason` derives
 * from it so the type and the list can't drift; the backend mirrors it as a
 * `Literal` + `VALID_SKIP_REASONS` (parity asserted in each repo's tests). */
export const SKIP_REASONS = ["separate_report", "wrong_comparison", "other"] as const;
export type SkipReason = (typeof SKIP_REASONS)[number];

/** Display labels for the skip-reason vocabulary — single source of truth
 * shared by the reason menu and the worklist badge tooltip. Keyed by
 * `SkipReason` so adding a reason without a label is a compile error (not just
 * a runtime-test failure). Consumers index it with the arbitrary server string
 * stored on a skip row via `SKIP_REASON_LABEL[reason as ...] ?? reason`, which
 * keeps the fallback for vocabulary drift. */
export const SKIP_REASON_LABEL: Record<SkipReason, string> = {
  separate_report: "Separate report exists",
  wrong_comparison: "Wrong comparison report",
  other: "Other",
};

export interface SkipInfo {
  reason: string;
  reason_note: string;
}

/** All of the caller's skipped cases, keyed by study_uid. */
export type SkippedMap = Record<string, SkipInfo>;

interface SkippedResponse {
  skipped: SkippedMap;
}

interface SkipArgs {
  cohortId: string;
  studyUid: string;
  skipped: boolean;
  reason?: SkipReason;
  reasonNote?: string;
}

const SKIPPED_ROOT = ["skipped-studies"] as const;

export const skippedKeys = {
  all: SKIPPED_ROOT,
  // Bulk cross-study key — one cache entry backs the whole worklist, shared
  // across single-cohort and merged multi-cohort views (and the case viewer).
  allStudies: [...SKIPPED_ROOT, "all-studies"] as const,
};

async function fetchAllSkipped(): Promise<SkippedMap> {
  const baseUrl = getApiBaseUrl();
  const response = await axios.get<SkippedResponse>(`${baseUrl}/api/reader/skipped`, {
    withCredentials: true,
  });
  return response.data.skipped;
}

async function postSkip({
  cohortId,
  studyUid,
  skipped,
  reason,
  reasonNote,
}: SkipArgs): Promise<void> {
  const baseUrl = getApiBaseUrl();
  const encodedCohort = encodeURIComponent(cohortId);
  const encodedUid = encodeURIComponent(studyUid);
  await axios.post(
    `${baseUrl}/api/reader/cohort/${encodedCohort}/study/${encodedUid}/skip`,
    { skipped, reason, reason_note: reasonNote },
    { withCredentials: true },
  );
}

/**
 * Bulk fetch of the caller's skipped cases across all studies, keyed by
 * study_uid. One request (one DynamoDB Query) backs the whole worklist — it
 * joins this against each row to drop skipped cases from the list. Mirrors the
 * per-user-overlay pattern of useAllSurfacerDecisions; `enabled` gates it on
 * auth / mode.
 */
export function useAllSkippedStudies(enabled: boolean) {
  return useQuery({
    queryKey: skippedKeys.allStudies,
    queryFn: fetchAllSkipped,
    enabled,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Toggle skip for a case. Optimistically updates the bulk overlay so the row
 * falls off (or returns) immediately, then invalidates to reconcile with the
 * server. Mirrors the optimistic pattern of useSurfacerDecisions.
 */
export function useSkipCase() {
  const queryClient = useQueryClient();
  const queryKey = skippedKeys.allStudies;

  const mutation = useMutation({
    mutationFn: postSkip,
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SkippedMap>(queryKey) ?? {};
      const next: SkippedMap = { ...previous };
      if (vars.skipped) {
        next[vars.studyUid] = {
          reason: vars.reason ?? "",
          reason_note: vars.reasonNote ?? "",
        };
      } else {
        delete next[vars.studyUid];
      }
      queryClient.setQueryData<SkippedMap>(queryKey, next);
      return { previous };
    },
    onError: (err, vars, context) => {
      console.error(
        `skip write failed for ${vars.studyUid} (skipped=${vars.skipped}); rolling back`,
        err,
      );
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    isError: mutation.isError,
    setSkipped: (args: SkipArgs) => {
      mutation.mutate(args);
    },
  };
}
